const SURVEY_TOKEN_STORAGE_PREFIX = "listy:survey-access-token:";
const inMemorySurveyTokens = new Map<number, string>();

const storageKey = (surveyId: number): string =>
  `${SURVEY_TOKEN_STORAGE_PREFIX}${surveyId}`;

const isPositiveSafeInteger = (value: number): boolean =>
  Number.isSafeInteger(value) && value > 0;

const normalizePathname = (pathname: string): string =>
  `/${pathname.replace(/^\/+/, "")}`;

export function storeSurveyAccessToken(
  surveyId: number,
  surveyToken: string,
): void {
  if (!isPositiveSafeInteger(surveyId) || !surveyToken.trim()) return;

  try {
    window.sessionStorage.setItem(storageKey(surveyId), surveyToken);
    inMemorySurveyTokens.delete(surveyId);
  } catch {
    inMemorySurveyTokens.set(surveyId, surveyToken);
  }
}

export function clearStoredSurveyAccessToken(surveyId: number): void {
  if (!isPositiveSafeInteger(surveyId)) return;

  inMemorySurveyTokens.delete(surveyId);
  try {
    window.sessionStorage.removeItem(storageKey(surveyId));
  } catch {
    // sessionStorageが使えない環境ではin-memory側の削除だけで十分
  }
}

export function getStoredSurveyAccessToken(surveyId: number): string {
  if (!isPositiveSafeInteger(surveyId)) return "";

  const inMemoryToken = inMemorySurveyTokens.get(surveyId);
  if (inMemoryToken) return inMemoryToken;

  try {
    return window.sessionStorage.getItem(storageKey(surveyId)) ?? "";
  } catch {
    return "";
  }
}

export function captureSurveyAccessTokenFromLocation(): void {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  url.pathname = normalizePathname(url.pathname);
  const fragmentParams = new URLSearchParams(url.hash.slice(1));
  const surveyToken = fragmentParams.get("surveyToken");
  const hasLegacyQueryToken = url.searchParams.has("surveyToken");
  if (surveyToken === null && !hasLegacyQueryToken) return;

  const match = url.pathname.match(/^\/mem\/survey\/(\d+)\/?$/);
  if (match && surveyToken !== null) {
    storeSurveyAccessToken(Number(match[1]), surveyToken);
  }

  // Query-based survey bearer tokens were never released. Scrub rather than
  // accepting them so a token cannot re-enter document/access logs.
  url.searchParams.delete("surveyToken");
  fragmentParams.delete("surveyToken");
  const remainingFragment = fragmentParams.toString();
  url.hash = remainingFragment ? `#${remainingFragment}` : "";
  // Path-only URLs that start with // are protocol-relative and would navigate
  // off-origin (e.g. //mem/survey/1 -> https://mem/survey/1).
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}
